import { useState } from "react";
import { Mail, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/v2/Toast";
import { useSendEmailVerify } from "@/lib/email-verify";
import { useUserMe } from "@/lib/user-me";

/**
 * Soft-gate banner shown at the top of every authenticated page when the
 * operator's email is not yet verified. Provides:
 *   - "Resend email" — issues a fresh magic link via the API
 *   - "I've verified" — refetches /users/me, dismissing the banner if the
 *     server has by then recorded `emailVerifiedAt`.
 *
 * The banner is dismissable per-session via in-memory state — the operator
 * can hide it for the current visit. Page reload re-shows until verified.
 */
export function EmailVerifyBanner() {
  const me = useUserMe();
  const send = useSendEmailVerify();
  const toast = useToast();
  const [hidden, setHidden] = useState(false);

  if (hidden) return null;
  if (me.isLoading || me.isError) return null;
  if (!me.data || me.data.emailVerifiedAt) return null;

  const onResend = () => {
    send.mutate(undefined, {
      onSuccess: () =>
        toast.success(
          "Verification email sent",
          `Check ${me.data?.email} for a link from us.`,
        ),
      onError: (e) =>
        toast.error("Couldn't send email", (e as Error).message),
    });
  };

  return (
    <div className="bg-status-bg-pending border-b border-status-pending/30 px-6 py-2 flex items-center gap-3 text-xs">
      <Mail className="h-3.5 w-3.5 text-status-pending shrink-0" />
      <span className="text-foreground">
        Verify your email — we sent a link to{" "}
        <strong className="font-medium">{me.data.email}</strong>. Until you
        click it, you can't make changes (new bookings, services, invites,
        plan) — viewing stays available.
      </span>
      <div className="ml-auto flex items-center gap-2 shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={onResend}
          disabled={send.isPending}
          className="h-7 px-2 text-xs"
        >
          <RefreshCw
            className={`h-3 w-3 mr-1 ${send.isPending ? "animate-spin" : ""}`}
          />
          {send.isPending ? "Sending…" : "Resend"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => me.refetch()}
          className="h-7 px-2 text-xs"
        >
          I've verified
        </Button>
        <button
          type="button"
          onClick={() => setHidden(true)}
          className="h-6 w-6 inline-flex items-center justify-center rounded text-muted-foreground hover:bg-muted/60"
          aria-label="Dismiss for this session"
          title="Hide until next refresh"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
